import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Mail, Lock, CheckCircle, AlertCircle, User, Fingerprint, Trash2, Edit2, Smartphone, Monitor, Tablet, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BASE_URL as API } from '../App';

// Helper function to detect device type from user agent
const detectDeviceType = () => {
  const ua = navigator.userAgent;
  
  if (/iPad/.test(ua)) return { type: 'tablet', name: 'iPad' };
  if (/iPhone/.test(ua)) return { type: 'phone', name: 'iPhone' };
  if (/Android/.test(ua) && /Mobile/.test(ua)) return { type: 'phone', name: 'Android Phone' };
  if (/Android/.test(ua)) return { type: 'tablet', name: 'Android Tablet' };
  if (/Macintosh/.test(ua)) return { type: 'desktop', name: 'Mac' };
  if (/Windows/.test(ua)) return { type: 'desktop', name: 'Windows PC' };
  if (/Linux/.test(ua)) return { type: 'desktop', name: 'Linux PC' };
  
  return { type: 'unknown', name: 'Device' };
};

// Get device icon based on type
const getDeviceIcon = (nickname) => {
  const lower = nickname?.toLowerCase() || '';
  if (lower.includes('iphone') || lower.includes('android phone') || lower.includes('phone') || lower.includes('mobile')) {
    return <Smartphone className="w-5 h-5 text-blue-500" />;
  }
  if (lower.includes('ipad') || lower.includes('tablet')) {
    return <Tablet className="w-5 h-5 text-purple-500" />;
  }
  if (lower.includes('mac') || lower.includes('windows') || lower.includes('linux') || lower.includes('pc') || lower.includes('desktop')) {
    return <Monitor className="w-5 h-5 text-green-500" />;
  }
  return <Fingerprint className="w-5 h-5 text-indigo-500" />;
};

// Helper to get biometric data from localStorage
const getLocalBiometricData = (email) => {
  if (!email) return null;
  const data = JSON.parse(localStorage.getItem('biometric_data') || '{}');
  return data[email.toLowerCase()];
};

export default function AccountSettings({ user }) {
  // Profile update state
  const [realName, setRealName] = useState(user?.real_name || '');
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Email change state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [mockEmailCode, setMockEmailCode] = useState('');

  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Biometric state
  const [showBiometricDialog, setShowBiometricDialog] = useState(false);
  const [serverCredentials, setServerCredentials] = useState([]);
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(true);
  const [editingCredential, setEditingCredential] = useState(null);
  const [editNickname, setEditNickname] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [localBiometricEnabled, setLocalBiometricEnabled] = useState(false);

  // Check localStorage for biometric data
  useEffect(() => {
    if (user?.email) {
      const localData = getLocalBiometricData(user.email);
      setLocalBiometricEnabled(localData?.enabled === true);
    }
  }, [user?.email]);

  // Fetch server-side biometric credentials on mount
  useEffect(() => {
    fetchServerCredentials();
  }, []);

  const fetchServerCredentials = async () => {
    setIsLoadingCredentials(true);
    try {
      const response = await axios.get(`${API}/api/auth/webauthn/credentials`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setServerCredentials(response.data || []);
    } catch (error) {
      console.error('Failed to fetch server credentials:', error);
      setServerCredentials([]);
    } finally {
      setIsLoadingCredentials(false);
    }
  };

  // Update profile information
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsUpdatingProfile(true);

    const profileData = {
      real_name: realName,
      display_name: displayName
    };

    try {
      const response = await axios.put(`${API}/api/auth/profile`, profileData, {
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}` 
        }
      });
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to update profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Delete a biometric credential from server
  const handleDeleteCredential = async (credentialId) => {
    try {
      await axios.delete(`${API}/api/auth/webauthn/credentials/${credentialId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Biometric credential deleted successfully');
      setShowDeleteConfirm(null);
      fetchServerCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete credential');
    }
  };

  // Update credential nickname
  const handleUpdateNickname = async () => {
    if (!editingCredential || !editNickname.trim()) return;
    
    try {
      await axios.patch(`${API}/api/auth/webauthn/credentials/${editingCredential}`, {
        nickname: editNickname.trim()
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Device name updated');
      setEditingCredential(null);
      setEditNickname('');
      fetchServerCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update device name');
    }
  };

  // Reset biometric "Never Ask" preference
  const hasBiometricNeverAsk = () => {
    const email = user?.email;
    if (!email) return false;
    const neverAskList = JSON.parse(localStorage.getItem('biometricNeverAsk') || '[]');
    return neverAskList.includes(email);
  };

  const handleResetBiometricPrompt = () => {
    const email = user?.email;
    if (!email) return;
    
    const neverAskList = JSON.parse(localStorage.getItem('biometricNeverAsk') || '[]');
    const updatedList = neverAskList.filter(e => e !== email);
    localStorage.setItem('biometricNeverAsk', JSON.stringify(updatedList));
    toast.success('Biometric prompt re-enabled. You will be asked on your next login.');
    window.location.reload();
  };

  // Full reset of all biometric data
  const handleFullBiometricReset = async () => {
    const email = user?.email;
    if (!email) return;
    
    try {
      // Delete all server-side credentials
      for (const cred of serverCredentials) {
        try {
          await axios.delete(`${API}/api/auth/webauthn/credentials/${cred.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        } catch (e) {
          console.warn('Failed to delete server credential:', e);
        }
      }
      
      // Clear local storage biometric data - correct keys
      // Remove this user's entry from biometric_data object
      const biometricData = JSON.parse(localStorage.getItem('biometric_data') || '{}');
      delete biometricData[email.toLowerCase()];
      localStorage.setItem('biometric_data', JSON.stringify(biometricData));
      
      // Clear last_biometric_email if it matches this user
      const lastBioEmail = localStorage.getItem('last_biometric_email');
      if (lastBioEmail && lastBioEmail.toLowerCase() === email.toLowerCase()) {
        localStorage.removeItem('last_biometric_email');
      }
      
      // Remove from "never ask" list
      const neverAskList = JSON.parse(localStorage.getItem('biometricNeverAsk') || '[]');
      const updatedList = neverAskList.filter(e => e.toLowerCase() !== email.toLowerCase());
      localStorage.setItem('biometricNeverAsk', JSON.stringify(updatedList));
      
      toast.success('All biometric data cleared. You will be prompted to set up biometrics on your next login.');
      
      // Update local state
      setLocalBiometricEnabled(false);
      
      // Refresh the credentials list
      fetchServerCredentials();
    } catch (error) {
      toast.error('Failed to reset biometrics: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Request email change
  const handleRequestEmailChange = async (e) => {
    e.preventDefault();
    
    if (!newEmail || newEmail === user.email) {
      toast.error('Please enter a different email address');
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/request-email-change`, {
        new_email: newEmail
      });
      
      setEmailCodeSent(true);
      setMockEmailCode(response.data.mock_code);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request email change');
    }
  };

  // Verify email change
  const handleVerifyEmailChange = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(`${API}/auth/verify-email-change`, {
        new_email: newEmail,
        verification_code: emailVerificationCode
      });
      
      toast.success('Email changed successfully! Please log in again.');
      setShowEmailDialog(false);
      setEmailCodeSent(false);
      setNewEmail('');
      setEmailVerificationCode('');
      setMockEmailCode('');
      
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify email change');
    }
  };

  // Change password directly
  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: user.email,
        old_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('Password changed successfully!');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Account Settings</h1>
        <p className="text-slate-600 mt-2">Manage your account information and security</p>
      </div>

      {/* Account Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Account Information
          </CardTitle>
          <CardDescription>Manage your personal information and account details</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="realName">Real Name</Label>
                <Input
                  id="realName"
                  value={realName}
                  onChange={(e) => setRealName(e.target.value)}
                  placeholder="SUPER ADMIN"
                />
                <p className="text-xs text-slate-500">Your legal name for official purposes</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="SUPER ADMIN"
                />
                <p className="text-xs text-slate-500">Used in welcome messages</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email Address</Label>
              <p className="text-sm text-slate-900">{user?.email}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Role</Label>
              <p className="text-sm text-slate-900 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={isUpdatingProfile}>
                {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
              </Button>
              <Button 
                type="button" 
                variant="outline"
                onClick={() => {
                  setRealName(user?.real_name || '');
                  setDisplayName(user?.display_name || '');
                }}
              >
                Reset
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Email Change Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Change Email Address
          </CardTitle>
          <CardDescription>Update your email address with verification</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowEmailDialog(true)}>
            Change Email
          </Button>
        </CardContent>
      </Card>

      {/* Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your password with verification</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowPasswordDialog(true)} data-testid="change-password-button">
            Change Password
          </Button>
        </CardContent>
      </Card>

      {/* Biometric Settings Card - Enhanced with Server Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5" />
            Biometric Login
          </CardTitle>
          <CardDescription>Manage your biometric authentication devices</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingCredentials ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500">Loading devices...</span>
            </div>
          ) : serverCredentials.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">
                    Biometric login enabled on {serverCredentials.length} device{serverCredentials.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    You can use your fingerprint or face to login on these devices
                  </p>
                </div>
              </div>
              
              {/* List of registered devices */}
              <div className="border rounded-lg divide-y">
                {serverCredentials.map((cred) => (
                  <div key={cred.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      {getDeviceIcon(cred.nickname)}
                      <div>
                        {editingCredential === cred.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editNickname}
                              onChange={(e) => setEditNickname(e.target.value)}
                              className="h-8 w-48"
                              placeholder="Device name"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleUpdateNickname();
                                if (e.key === 'Escape') {
                                  setEditingCredential(null);
                                  setEditNickname('');
                                }
                              }}
                            />
                            <Button size="sm" onClick={handleUpdateNickname}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => {
                              setEditingCredential(null);
                              setEditNickname('');
                            }}>Cancel</Button>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-slate-900">{cred.nickname || 'Unnamed Device'}</p>
                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                              <span>Added: {formatDate(cred.created_at)}</span>
                              {cred.last_used && (
                                <span>• Last used: {formatDate(cred.last_used)}</span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {editingCredential !== cred.id && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingCredential(cred.id);
                            setEditNickname(cred.nickname || '');
                          }}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowDeleteConfirm(cred.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : localBiometricEnabled ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">
                    Biometric login enabled on this device
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    You can use your fingerprint or face to login on this device
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-slate-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">No biometric devices registered</p>
                <p className="text-xs text-slate-600 mt-1">
                  Enable biometric login during your next mobile login. Your device will be registered automatically.
                </p>
              </div>
            </div>
          )}
          
          {/* Show "Never Ask" status and reset option */}
          {hasBiometricNeverAsk() && (
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900">Biometric prompt disabled</p>
                <p className="text-xs text-amber-700 mt-1">You selected &quot;Never ask again&quot; for biometric setup prompts</p>
                <Button 
                  variant="link" 
                  onClick={handleResetBiometricPrompt}
                  className="p-0 h-auto text-xs text-amber-700 hover:text-amber-900 mt-1"
                >
                  Re-enable biometric prompts
                </Button>
              </div>
            </div>
          )}
          
          {/* Reset All Biometrics Button */}
          <div className="pt-4 border-t">
            <Button 
              variant="outline"
              onClick={handleFullBiometricReset}
              className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
              data-testid="reset-biometrics-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Reset All Biometrics
            </Button>
            <p className="text-xs text-slate-500 mt-2">
              This will remove all registered devices and re-enable the biometric setup prompt on your next login.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Email Change Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={(open) => {
        setShowEmailDialog(open);
        if (!open) {
          setEmailCodeSent(false);
          setNewEmail('');
          setEmailVerificationCode('');
          setMockEmailCode('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Email Address</DialogTitle>
            <DialogDescription>
              {!emailCodeSent 
                ? 'Enter your new email address. A verification code will be sent to the new email.'
                : 'Enter the 6-digit verification code sent to your new email.'}
            </DialogDescription>
          </DialogHeader>

          {!emailCodeSent ? (
            <form onSubmit={handleRequestEmailChange} className="space-y-4">
              <div>
                <Label>Current Email</Label>
                <Input value={user?.email} disabled />
              </div>
              <div>
                <Label>New Email Address</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="new.email@example.com"
                  required
                  data-testid="new-email-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="request-email-code-button">
                Send Verification Code
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Mock Email: Verification code is <strong>{mockEmailCode}</strong>
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  In production, this would be sent to {newEmail}
                </p>
              </div>

              <form onSubmit={handleVerifyEmailChange} className="space-y-4">
                <div>
                  <Label>Verification Code</Label>
                  <Input
                    type="text"
                    value={emailVerificationCode}
                    onChange={(e) => setEmailVerificationCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    required
                    data-testid="email-verification-code-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="verify-email-code-button">
                  Verify and Change Email
                </Button>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(open) => {
        setShowPasswordDialog(open);
        if (!open) {
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new password.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                data-testid="new-password-input"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="change-password-button">
              Change Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Credential Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={(open) => !open && setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Remove Biometric Device
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this device? You will no longer be able to use biometric login from it.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(null)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteCredential(showDeleteConfirm)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Remove Device
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
